import { useCallback, useEffect, useRef, useState } from "react"
import { laplacianVariance, toGray } from "./frame-metrics"
import { findWideLens, type WideLens } from "./lens"
import { stillCamera, type Size, type StillCamera } from "./still"
import type { StreamSource } from "./stream-source"

// The live back camera for the whole walk — ONE stream, opened from a tap
// (iOS needs the gesture for play()), never torn down between shots.
//
//   - backgrounded / screen locked → stream stopped (saves battery; iOS ends
//     it anyway) and reopened when the page is visible again;
//   - a stream that dies on its own is reopened once, then the screen says
//     "Tap to resume";
//   - torch only when the track says it supports it (most iPhones don't);
//   - continuous focus when supported; the screen stays awake while live.
//
// capture() takes the phone's own still photo where the browser can
// (Android Chrome: still.ts), else grabs the full video frame (more than the
// on-screen crop, never less) as a JPEG: the sharpest of a short burst, so a
// hand that moves a little at the tap (or the auto-photo landing mid-sway)
// costs a frame, not the photo. One still that fails, comes back wrong or
// stalls the live picture, and the rest of the visit uses the burst.
//
// The wide lens ("0.5×", lens.ts) where the phone has one: switched by
// zooming out, or by opening that camera instead (a quick blink, like the
// phone's own camera).

export type CameraStatus = "idle" | "starting" | "live" | "denied" | "unavailable" | "interrupted"

export interface CameraPhoto {
  blob: Blob
  // The phone's own still, or the sharpest of a burst of video frames.
  via: "still" | "burst"
  // The live picture's size when it was taken (a still is cut to its shape).
  frame: Size
}

export type Lens = "normal" | "wide"

const PRIMARY: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30, max: 30 },
  },
}
const FALLBACK: MediaStreamConstraints = { audio: false, video: { facingMode: "environment" } }

interface TorchCapabilities extends MediaTrackCapabilities {
  torch?: boolean
  focusMode?: string[]
}

interface WakeLockSentinelLike {
  release(): Promise<void>
}

function errorStatus(err: unknown): CameraStatus {
  const name = err instanceof DOMException || err instanceof Error ? err.name : ""
  return name === "NotAllowedError" || name === "SecurityError" ? "denied" : "unavailable"
}

export function useCamera({ active, source }: { active: boolean; source: StreamSource | null }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const wakeRef = useRef<WakeLockSentinelLike | null>(null)
  const genRef = useRef(0)
  const autoRestarts = useRef(0)
  const stillRef = useRef<StillCamera | null>(null)
  // Set for the rest of the visit once a still lets us down.
  const stillsOff = useRef(false)
  // The wide lens, once found (looked for once, after the camera is allowed).
  const wideRef = useRef<WideLens | null>(null)
  const lookedForWide = useRef(false)
  const lensRef = useRef<Lens>("normal")
  const [lens, setLensState] = useState<Lens>("normal")
  // The wide lens's button label ("0.5×"; a zoom says how far it goes), or
  // null: none.
  const [wideLabel, setWideLabel] = useState<string | null>(null)
  // The same camera opening again (a lens switch): the last picture stays
  // up, softened, until the new one plays (never a black blink), and
  // nothing can be taken meanwhile.
  const [switching, setSwitching] = useState<{ poster: string | null } | null>(null)
  const [status, setStatus] = useState<CameraStatus>("idle")
  const [torch, setTorchState] = useState({ supported: false, on: false })
  const [track, setTrack] = useState<MediaStreamTrack | null>(null)

  const releaseWake = useCallback(() => {
    const lock = wakeRef.current
    wakeRef.current = null
    void lock?.release().catch(() => {})
  }, [])

  const requestWake = useCallback(async () => {
    const nav = navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> } }
    if (!nav.wakeLock || wakeRef.current) return
    try {
      wakeRef.current = await nav.wakeLock.request("screen")
    } catch {
      // Not allowed (battery saver, iframe) — the camera still works.
    }
  }, [])

  const forgetWide = useCallback(() => {
    wideRef.current = null
    setWideLabel(null)
    lensRef.current = "normal"
    setLensState("normal")
  }, [])

  const stop = useCallback(() => {
    genRef.current++
    const stream = streamRef.current
    streamRef.current = null
    stream?.getTracks().forEach((t) => t.stop())
    if (videoRef.current) videoRef.current.srcObject = null
    stillRef.current = null
    setTrack(null)
    setTorchState({ supported: false, on: false })
    releaseWake()
  }, [releaseWake])

  // `quiet`: the same camera opened again (a lens switch, a stalled
  // picture): the screen keeps showing it as live.
  const open = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!source) return
    if (quiet) setSwitching({ poster: snapshot(videoRef.current) })
    stop()
    const gen = genRef.current
    if (!quiet) setStatus("starting")
    const wide = wideRef.current
    const wideDevice = lensRef.current === "wide" && wide?.kind === "device" ? wide.deviceId : null
    let stream: MediaStream
    try {
      try {
        stream = await source.open(
          wideDevice ? { audio: false, video: { ...(PRIMARY.video as MediaTrackConstraints), facingMode: undefined, deviceId: { exact: wideDevice } } } : PRIMARY,
        )
      } catch (err) {
        const name = err instanceof Error ? err.name : ""
        if (!wideDevice && name !== "OverconstrainedError" && name !== "ConstraintNotSatisfiedError") throw err
        // (A newer open is under way: that one decides.)
        if (gen !== genRef.current) return
        // The wide lens wouldn't open: the usual one, and no wide lens for
        // the rest of the visit.
        if (wideDevice) forgetWide()
        stream = await source.open(wideDevice ? PRIMARY : FALLBACK)
      }
    } catch (err) {
      if (gen === genRef.current) {
        setStatus(errorStatus(err))
        setSwitching(null)
      }
      return
    }
    if (gen !== genRef.current) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }
    streamRef.current = stream
    const videoTrack = stream.getVideoTracks()[0] ?? null

    const caps = (videoTrack?.getCapabilities?.() ?? {}) as TorchCapabilities & { zoom?: { min?: number; max?: number } }
    if (caps.focusMode?.includes("continuous")) {
      await videoTrack
        ?.applyConstraints({ advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet] })
        .catch(() => {})
    }
    setTorchState({ supported: caps.torch === true, on: false })

    videoTrack?.addEventListener("ended", () => {
      if (gen !== genRef.current || document.visibilityState !== "visible") return
      if (autoRestarts.current < 1) {
        autoRestarts.current++
        void open()
      } else {
        setStatus("interrupted")
      }
    })

    const video = videoRef.current
    if (video) {
      video.muted = true
      video.playsInline = true
      video.srcObject = stream
      await video.play().catch(() => {
        // autoplay can reject silently; <video autoPlay muted playsInline> recovers
      })
    }
    if (gen !== genRef.current) return
    stillRef.current = videoTrack && !stillsOff.current ? stillCamera(videoTrack) : null
    setTrack(videoTrack)
    setStatus("live")
    setSwitching(null)
    void requestWake()
    // Where the wide lens is a zoom, it's set on the open camera.
    if (lensRef.current === "wide" && wide?.kind === "zoom") {
      void videoTrack?.applyConstraints({ advanced: [{ zoom: wide.zoom } as MediaTrackConstraintSet] }).catch(() => {})
    }
    // Look for a wide lens once (the cameras have names now).
    if (!lookedForWide.current) {
      lookedForWide.current = true
      const devices = await source.devices().catch(() => [])
      const found = findWideLens(devices, caps.zoom)
      wideRef.current = found
      setWideLabel(found ? (found.kind === "zoom" ? `${Math.round(found.zoom * 10) / 10}×` : "0.5×") : null)
    }
  }, [source, stop, requestWake, forgetWide])

  const setLens = useCallback(
    (next: Lens) => {
      const wide = wideRef.current
      if (next === lensRef.current || (next === "wide" && !wide)) return
      lensRef.current = next
      setLensState(next)
      if (wide?.kind === "zoom") {
        const t = streamRef.current?.getVideoTracks()[0]
        void t
          ?.applyConstraints({ advanced: [{ zoom: next === "wide" ? wide.zoom : 1 } as MediaTrackConstraintSet] })
          .catch(() => {
            if (next === "wide") forgetWide()
          })
      } else {
        void open({ quiet: true })
      }
    },
    [open, forgetWide],
  )

  // Open while active; close on the way out.
  useEffect(() => {
    if (!active || !source) return
    autoRestarts.current = 0
    void open()
    return () => {
      stop()
      setStatus("idle")
    }
  }, [active, source, open, stop])

  // Backgrounded / locked → stop; visible again → reopen. pagehide → stop.
  useEffect(() => {
    if (!active) return
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        stop()
        setStatus("idle")
      } else {
        autoRestarts.current = 0
        void open()
      }
    }
    const onPageHide = () => stop()
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("pagehide", onPageHide)
    return () => {
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("pagehide", onPageHide)
    }
  }, [active, open, stop])

  const setTorch = useCallback(
    async (on: boolean) => {
      const t = streamRef.current?.getVideoTracks()[0]
      if (!t || !torch.supported) return
      try {
        await t.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] })
        setTorchState((s) => ({ ...s, on }))
      } catch {
        setTorchState({ supported: false, on: false })
      }
    },
    [torch.supported],
  )

  const torchOn = useRef(false)
  useEffect(() => {
    torchOn.current = torch.on
  }, [torch.on])

  const capture = useCallback(async (): Promise<CameraPhoto | null> => {
    const video = videoRef.current
    if (!video || !video.videoWidth || !video.videoHeight) return null
    const frame = { w: video.videoWidth, h: video.videoHeight }
    const gen = genRef.current
    // Not with the light on: a still switches it off on some phones.
    const still = torchOn.current ? null : stillRef.current
    if (still) {
      const { photo, tried } = await still.take(frame)
      // The camera closed or changed meanwhile: nothing more to do here.
      if (gen !== genRef.current) return photo ? { blob: photo, via: "still", frame } : null
      if (tried) {
        // The phone was turned while the still was taken: the shapes can't
        // match, and it's not the camera's fault. Stills stay on.
        const turned = video.videoWidth !== frame.w || video.videoHeight !== frame.h
        if (!photo && turned) {
          const t = streamRef.current?.getVideoTracks()[0]
          stillRef.current = t ? stillCamera(t) : null
          const blob = await burst(video)
          return blob ? { blob, via: "burst", frame: { w: video.videoWidth, h: video.videoHeight } } : null
        }
        // Some phones freeze the live picture after a still (or one that
        // hung): if no new frame comes, stills are off and the camera opens
        // again, quietly.
        const moving = frameArrives(video, STALL_MS).then((ok) => {
          if (!ok && gen === genRef.current) {
            stillsOff.current = true
            still.giveUp()
            autoRestarts.current = 0
            void open({ quiet: true })
          }
          return ok
        })
        if (photo) return { blob: photo, via: "still", frame }
        // It let this camera down: the burst from now on (but only once the
        // picture is known to be moving, never a frozen frame).
        stillsOff.current = true
        if (!(await moving) || gen !== genRef.current || !video.videoWidth) return null
      }
    }
    const blob = await burst(video)
    return blob ? { blob, via: "burst", frame } : null
  }, [open])

  const restart = useCallback(() => {
    autoRestarts.current = 0
    void open()
  }, [open])

  return {
    videoRef,
    status,
    torch,
    setTorch,
    capture,
    restart,
    track,
    lens,
    wideLabel,
    setLens,
    switching: !!switching,
    poster: switching?.poster ?? null,
  }
}

// Four frames at 30 fps: about 130 ms.
const BURST = 4
const PROBE_WIDTH = 480
// No new live frame this long after a still: the phone froze the picture.
const STALL_MS = 2000

// The sharpest of a short burst of video frames, as a JPEG.
async function burst(video: HTMLVideoElement): Promise<Blob | null> {
  const w = video.videoWidth
  const h = video.videoHeight
  const probe = document.createElement("canvas")
  let best: HTMLCanvasElement | null = null
  let bestScore = -1
  let spare: HTMLCanvasElement | null = null
  for (let i = 0; i < BURST; i++) {
    if (i > 0) await nextFrame(video)
    if (!video.videoWidth) break
    const canvas: HTMLCanvasElement = spare ?? document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) break
    ctx.drawImage(video, 0, 0, w, h)
    const score = sharpness(canvas, probe)
    if (score > bestScore) {
      spare = best
      best = canvas
      bestScore = score
    } else {
      spare = canvas
    }
  }
  // Full-size canvases are heavy on a phone (iOS caps canvas memory): give
  // them back as soon as the photo is out.
  const release = () => {
    for (const c of [best, spare, probe]) {
      if (!c) continue
      c.width = 0
      c.height = 0
    }
  }
  const photo = best
  if (!photo) {
    release()
    return null
  }
  return new Promise((resolve) =>
    photo.toBlob(
      (b) => {
        release()
        resolve(b)
      },
      "image/jpeg",
      0.9,
    ),
  )
}

// The picture on screen now, small (it's shown softened while the camera
// switches lens), or null.
function snapshot(video: HTMLVideoElement | null): string | null {
  if (!video?.videoWidth || !video.videoHeight) return null
  try {
    const canvas = document.createElement("canvas")
    canvas.width = 240
    canvas.height = Math.max(1, Math.round((video.videoHeight * 240) / video.videoWidth))
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL("image/jpeg", 0.7)
  } catch {
    return null
  }
}

// Whether a new video frame shows up within `ms` (true where the browser
// can't say).
function frameArrives(video: HTMLVideoElement, ms: number): Promise<boolean> {
  const v = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }
  if (!v.requestVideoFrameCallback) return Promise.resolve(true)
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), ms)
    v.requestVideoFrameCallback!(() => {
      clearTimeout(timer)
      resolve(true)
    })
  })
}

// The next new video frame (or ~one frame's time where the browser can't say).
function nextFrame(video: HTMLVideoElement): Promise<void> {
  const v = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }
  return new Promise((resolve) => {
    const fallback = setTimeout(resolve, 60)
    if (v.requestVideoFrameCallback) {
      v.requestVideoFrameCallback(() => {
        clearTimeout(fallback)
        resolve()
      })
    }
  })
}

// Edge energy of the middle of a frame, on a medium copy (fine enough to see
// a few pixels of smear, quick enough for four frames).
function sharpness(frame: HTMLCanvasElement, probe: HTMLCanvasElement): number {
  const sw = frame.width * 0.7
  const sh = frame.height * 0.7
  const pw = Math.min(PROBE_WIDTH, Math.round(sw))
  const ph = Math.max(1, Math.round((sh / sw) * pw))
  probe.width = pw
  probe.height = ph
  const ctx = probe.getContext("2d", { willReadFrequently: true })
  if (!ctx) return 0
  ctx.drawImage(frame, (frame.width - sw) / 2, (frame.height - sh) / 2, sw, sh, 0, 0, pw, ph)
  return laplacianVariance(toGray(ctx.getImageData(0, 0, pw, ph).data, pw, ph))
}
